import { useState, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  ShieldCheck, Search, Plus, CheckCircle2, X, Edit3, AlertCircle, Building2,
  GitMerge
} from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import Card, { CardHeader } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import Skeleton from '../../components/ui/Skeleton';
import { useShop } from '../../hooks/useShop';
import { useAuth } from '../../contexts/AuthContext';
import { useVendorProfiles } from '../../hooks/useVendorData';
import { api } from '../../api/client';

const STATUS_TONES = {
  DRAFT: 'neutral',
  SUBMITTED: 'action',
  APPROVED: 'success',
  REJECTED: 'danger',
};

const CATEGORY_TONES = {
  GENERAL_AUTO: 'insight',
  TRANSMISSION: 'recovery',
  SHOP_SUPPLIES: 'action',
  SUBLET: 'neutral',
  IGNORED: 'neutral',
};

const TABS = [
  { key: 'all', label: 'All', filter: null },
  { key: 'pending', label: 'Pending Approval', filter: 'SUBMITTED' },
  { key: 'approved', label: 'Approved', filter: 'APPROVED' },
  { key: 'rejected', label: 'Rejected', filter: 'REJECTED' },
];

export default function VendorProfilesPage() {
  const { currentShopId, currentShop, shops } = useShop();
  const { isAllShops } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [editingProfile, setEditingProfile] = useState(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [rejectingProfile, setRejectingProfile] = useState(null);
  const [shopFilter, setShopFilter] = useState(isAllShops ? '' : (currentShopId || ''));
  const effectiveShopId = shopFilter || (isAllShops ? null : currentShopId);

  const filterStatus = TABS.find((t) => t.key === activeTab)?.filter;
  const { profiles, loading, refresh } = useVendorProfiles({
    shopId: effectiveShopId,
    approvalStatus: filterStatus,
  });
  const [mergingProfile, setMergingProfile] = useState(null);

  const filtered = useMemo(() => {
    if (!search) return profiles;
    const s = search.toLowerCase();
    return profiles.filter(
      (p) =>
        p.canonical_name.toLowerCase().includes(s) ||
        (p.aliases || []).some((a) => a.toLowerCase().includes(s))
    );
  }, [profiles, search]);

  const counts = useMemo(() => {
    const c = { all: 0, SUBMITTED: 0, APPROVED: 0, REJECTED: 0, DRAFT: 0 };
    profiles.forEach((p) => {
      c.all++;
      c[p.approval_status] = (c[p.approval_status] || 0) + 1;
    });
    return c;
  }, [profiles]);

  return (
    <div>
      <PageHeader
        title="Vendor Profiles"
        description={`${shopFilter ? (shops.find(s => s.shop_id === Number(shopFilter))?.shop_nickname || 'Shop') : 'All shops'} · ${counts.all} profiles · ${counts.SUBMITTED || 0} awaiting approval`}
        actions={
          <Button variant="primary" leftIcon={<Plus size={16} />} onClick={() => setCreatingNew(true)}>
            New profile
          </Button>
        }
      />

      {isAllShops && shops.length > 0 && (
        <div className="flex items-center gap-3 mb-4 text-[13px]">
          <span className="text-[var(--color-warm-500)] font-semibold uppercase text-[10px] tracking-wide">Shop:</span>
          <select
            value={shopFilter}
            onChange={(e) => setShopFilter(e.target.value)}
            className="h-9 px-3 bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] text-[13px] font-semibold text-[var(--color-warm-800)] focus:outline-none focus:border-[var(--color-insight-500)]"
          >
            <option value="">All shops</option>
            {shops.map((s) => (
              <option key={s.shop_id} value={s.shop_id}>
                {s.shop_nickname || s.shop_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-[var(--color-warm-100)]">
        {TABS.map((t) => {
          const tabCount = t.filter ? counts[t.filter] : counts.all;
          const isActive = activeTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 h-10 text-[13px] font-semibold border-b-2 transition-colors -mb-px ${
                isActive
                  ? 'border-[var(--color-warm-900)] text-[var(--color-warm-900)]'
                  : 'border-transparent text-[var(--color-warm-500)] hover:text-[var(--color-warm-800)]'
              }`}
            >
              {t.label}
              {tabCount > 0 && (
                <span className={`ml-1.5 text-[11px] px-1.5 py-0.5 rounded-full tabular-nums ${
                  isActive ? 'bg-[var(--color-warm-900)] text-[var(--color-cream-50)]' : 'bg-[var(--color-cream-200)] text-[var(--color-warm-600)]'
                }`}>
                  {tabCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="mb-4 max-w-sm">
        <Input
          placeholder="Search by name or alias…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leftIcon={<Search size={14} />}
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => <Skeleton key={i} height={70} />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <Building2 size={36} className="text-[var(--color-warm-400)] mx-auto mb-2" />
            <div className="text-[14px] font-semibold text-[var(--color-warm-900)]">
              {search ? 'No matches' : `No ${activeTab !== 'all' ? activeTab + ' ' : ''}profiles`}
            </div>
            <div className="text-[12px] text-[var(--color-warm-500)] mt-1">
              {activeTab === 'pending' ? 'Submitted profiles awaiting your approval will appear here.' : 'Profiles will appear here as they\'re created or detected from scans.'}
            </div>
          </div>
        </Card>
      ) : (
        <Card padded={false}>
          <ul className="divide-y divide-[var(--color-warm-100)]">
            {filtered.map((p) => (
              <ProfileRow
                key={p.id}
                profile={p}
                onEdit={() => setEditingProfile(p)}
                onApprove={async () => { await api.approveVendorProfile(p.id); refresh(); }}
                onReject={() => setRejectingProfile(p)}
                onMerge={() => setMergingProfile(p)}
              />
            ))}
          </ul>
        </Card>
      )}

      {mergingProfile && (
        <MergeModal
          source={mergingProfile}
          allProfiles={profiles.filter((p) => p.id !== mergingProfile.id && p.shop_id === mergingProfile.shop_id && p.approval_status === 'APPROVED')}
          onClose={() => setMergingProfile(null)}
          onMerged={() => { setMergingProfile(null); refresh(); }}
        />
      )}

      {/* Edit / Create modal */}
      {(editingProfile || creatingNew) && (
        <ProfileModal
          profile={editingProfile}
          shopId={currentShopId}
          onClose={() => { setEditingProfile(null); setCreatingNew(false); }}
          onSaved={() => { setEditingProfile(null); setCreatingNew(false); refresh(); }}
        />
      )}

      {/* Reject modal */}
      {rejectingProfile && (
        <RejectModal
          profile={rejectingProfile}
          onClose={() => setRejectingProfile(null)}
          onRejected={() => { setRejectingProfile(null); refresh(); }}
        />
      )}
    </div>
  );
}

function ProfileRow({ profile, onEdit, onApprove, onReject, onMerge }) {
  return (
    <li className="px-5 py-4 hover:bg-[var(--color-cream-100)] transition-colors">
      <div className="flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[14px] font-semibold text-[var(--color-warm-900)]">{profile.canonical_name}</span>
            <Badge tone={CATEGORY_TONES[profile.vendor_category]} size="xs">
              {profile.vendor_category.replace('_', ' ')}
            </Badge>
            <Badge tone="neutral" size="xs">{profile.payment_method}</Badge>
            <Badge tone={STATUS_TONES[profile.approval_status]} size="xs" dot>
              {profile.approval_status}
            </Badge>
            {profile.statement_cadence && (
              <span className="text-[11px] text-[var(--color-warm-500)]">
                {profile.statement_cadence.toLowerCase()}
              </span>
            )}
          </div>
          {profile.aliases?.length > 0 && (
            <div className="text-[11px] text-[var(--color-warm-500)] flex flex-wrap gap-1">
              <span className="font-semibold uppercase tracking-wide">Aliases:</span>
              {profile.aliases.slice(0, 5).map((a, i) => (
                <span key={i} className="px-1.5 py-0.5 bg-[var(--color-cream-200)] rounded text-[var(--color-warm-700)]">{a}</span>
              ))}
              {profile.aliases.length > 5 && (
                <span className="text-[var(--color-warm-400)]">+{profile.aliases.length - 5} more</span>
              )}
            </div>
          )}
          {profile.rejection_reason && (
            <div className="text-[11px] text-[var(--color-danger-600)] mt-1.5 flex items-start gap-1">
              <AlertCircle size={11} className="mt-0.5 shrink-0" />
              <span>Rejected: {profile.rejection_reason}</span>
            </div>
          )}
        </div>

        <div className="shrink-0 flex items-center gap-2">
          {profile.approval_status === 'SUBMITTED' && (
            <>
              <Button variant="ghost" size="sm" leftIcon={<X size={14} />} onClick={onReject}>Reject</Button>
              <Button variant="accent" size="sm" leftIcon={<CheckCircle2 size={14} />} onClick={onApprove}>Approve</Button>
            </>
          )}
          {profile.approval_status === 'APPROVED' && onMerge && (
            <Button variant="ghost" size="sm" leftIcon={<GitMerge size={14} />} onClick={onMerge} title="Merge into another vendor">
              Merge
            </Button>
          )}
          <Button variant="ghost" size="sm" leftIcon={<Edit3 size={14} />} onClick={onEdit}>Edit</Button>
        </div>
      </div>
    </li>
  );
}

function MergeModal({ source, allProfiles, onClose, onMerged }) {
  const [targetId, setTargetId] = useState('');
  const [mergeAliases, setMergeAliases] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function merge() {
    if (!targetId) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.mergeVendorProfiles(source.id, Number(targetId), mergeAliases);
      onMerged();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={`Merge "${source.canonical_name}"`}
      description="This vendor is a duplicate of another. Combine them so future scans route to one profile."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={merge} loading={submitting} disabled={!targetId}>
            Merge + Move all checks
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="p-3 rounded-[var(--radius-md)] bg-[var(--color-action-50)] border border-[var(--color-action-100)] text-[12px] text-[var(--color-action-700)]">
          <strong>What happens:</strong> All COD checks currently tagged with "{source.canonical_name}" will be re-tagged to the chosen vendor. The source is soft-deleted (marked REJECTED with reason "merged"). If "Merge aliases" is on, all of this vendor's aliases + canonical name will be added to the target's aliases.
        </div>

        <div>
          <label className="block text-[12px] font-semibold text-[var(--color-warm-700)] mb-1.5">
            Merge into:
          </label>
          <select
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            className="w-full h-10 bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] px-3 text-[14px]"
            autoFocus
          >
            <option value="">— Select target vendor —</option>
            {allProfiles.map((p) => (
              <option key={p.id} value={p.id}>
                {p.canonical_name} ({p.vendor_category})
              </option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            checked={mergeAliases}
            onChange={(e) => setMergeAliases(e.target.checked)}
            className="w-4 h-4"
          />
          <span>Add "{source.canonical_name}" and its aliases to the target's aliases (recommended)</span>
        </label>

        {error && (
          <div className="p-3 rounded-[var(--radius-md)] bg-[var(--color-danger-50)] border border-[var(--color-danger-100)] text-[13px] text-[var(--color-danger-700)]">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

function ProfileModal({ profile, shopId, onClose, onSaved }) {
  const isNew = !profile;
  const [form, setForm] = useState({
    canonical_name: profile?.canonical_name || '',
    aliases: (profile?.aliases || []).join(', '),
    is_parts_vendor: profile?.is_parts_vendor !== false,
    vendor_category: profile?.vendor_category || 'GENERAL_AUTO',
    payment_method: profile?.payment_method || 'COD',
    statement_cadence: profile?.statement_cadence || '',
    typical_part_keywords: (profile?.typical_part_keywords || []).join(', '),
    atypical_part_keywords: (profile?.atypical_part_keywords || []).join(', '),
    notes: profile?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [submitForApproval, setSubmitForApproval] = useState(false);
  const [error, setError] = useState(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        aliases: form.aliases.split(',').map((s) => s.trim()).filter(Boolean),
        typical_part_keywords: form.typical_part_keywords.split(',').map((s) => s.trim()).filter(Boolean),
        atypical_part_keywords: form.atypical_part_keywords.split(',').map((s) => s.trim()).filter(Boolean),
        statement_cadence: form.statement_cadence || null,
        shop_id: shopId,
      };
      if (isNew) {
        payload.submit_immediately = submitForApproval;
        await api.createVendorProfile(payload);
      } else {
        await api.updateVendorProfile(profile.id, payload);
      }
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={isNew ? 'New Vendor Profile' : `Edit: ${profile.canonical_name}`}
      description={isNew ? 'Add a vendor that will appear in scans for this shop.' : 'Update the profile metadata.'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={save} loading={saving}>
            {isNew && submitForApproval ? 'Create & Submit' : isNew ? 'Save as Draft' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Canonical name"
          value={form.canonical_name}
          onChange={(e) => setForm({ ...form, canonical_name: e.target.value })}
          required
          className="col-span-2"
        />
        <Input
          label="Aliases (comma-separated)"
          value={form.aliases}
          onChange={(e) => setForm({ ...form, aliases: e.target.value })}
          hint="Common spelling variants, abbreviations, etc."
          className="col-span-2"
        />
        <div>
          <label className="block text-[12px] font-semibold text-[var(--color-warm-700)] mb-1.5">Category</label>
          <select
            value={form.vendor_category}
            onChange={(e) => setForm({ ...form, vendor_category: e.target.value })}
            className="w-full h-10 bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] px-3 text-[14px] text-[var(--color-warm-900)] focus:outline-none focus:border-[var(--color-insight-500)] focus:ring-2 focus:ring-[var(--color-insight-100)]"
          >
            <option value="GENERAL_AUTO">General Automotive</option>
            <option value="TRANSMISSION">Transmission</option>
            <option value="SHOP_SUPPLIES">Shop Supplies</option>
            <option value="SUBLET">Sublet</option>
            <option value="IGNORED">Ignored</option>
          </select>
        </div>
        <div>
          <label className="block text-[12px] font-semibold text-[var(--color-warm-700)] mb-1.5">Payment Method</label>
          <select
            value={form.payment_method}
            onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
            className="w-full h-10 bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] px-3 text-[14px]"
          >
            <option value="COD">COD</option>
            <option value="STATEMENT">Statement</option>
            <option value="MIXED">Mixed</option>
          </select>
        </div>
        {form.payment_method === 'STATEMENT' && (
          <div>
            <label className="block text-[12px] font-semibold text-[var(--color-warm-700)] mb-1.5">Statement Cadence</label>
            <select
              value={form.statement_cadence}
              onChange={(e) => setForm({ ...form, statement_cadence: e.target.value })}
              className="w-full h-10 bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] px-3 text-[14px]"
            >
              <option value="">— Select —</option>
              <option value="WEEKLY">Weekly</option>
              <option value="BIWEEKLY">Biweekly</option>
              <option value="MONTHLY">Monthly</option>
              <option value="ON_DEMAND">On Demand</option>
            </select>
          </div>
        )}
        {form.vendor_category === 'SHOP_SUPPLIES' && (
          <>
            <Input
              label="Typical items (comma-separated)"
              value={form.typical_part_keywords}
              onChange={(e) => setForm({ ...form, typical_part_keywords: e.target.value })}
              hint="e.g., rags, brake clean, wd-40"
              className="col-span-2"
            />
            <Input
              label="Atypical items to flag (comma-separated)"
              value={form.atypical_part_keywords}
              onChange={(e) => setForm({ ...form, atypical_part_keywords: e.target.value })}
              hint="e.g., brake pad, rotor — items that shouldn't be on this vendor's invoice"
              className="col-span-2"
            />
          </>
        )}
        <div className="col-span-2">
          <label className="block text-[12px] font-semibold text-[var(--color-warm-700)] mb-1.5">Notes</label>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2}
            className="w-full bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] p-3 text-[14px]"
          />
        </div>
        {isNew && (
          <label className="col-span-2 flex items-center gap-2 text-[13px] text-[var(--color-warm-700)]">
            <input
              type="checkbox"
              checked={submitForApproval}
              onChange={(e) => setSubmitForApproval(e.target.checked)}
              className="w-4 h-4"
            />
            Submit immediately for auditor approval (otherwise saved as Draft)
          </label>
        )}
        {error && (
          <div className="col-span-2 p-3 rounded-[var(--radius-md)] bg-[var(--color-danger-50)] border border-[var(--color-danger-100)] text-[13px] text-[var(--color-danger-700)] flex gap-2">
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </Modal>
  );
}

function RejectModal({ profile, onClose, onRejected }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function reject() {
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      await api.rejectVendorProfile(profile.id, reason);
      onRejected();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={`Reject: ${profile.canonical_name}`}
      description="The submitter will see this reason and can resubmit after fixing."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={reject} loading={submitting} disabled={!reason.trim()}>
            Reject
          </Button>
        </>
      }
    >
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={4}
        placeholder="Why is this profile being rejected?"
        className="w-full bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] p-3 text-[14px]"
        autoFocus
      />
    </Modal>
  );
}
